import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Prioritized loading - load critical images first
const CRITICAL_IMAGES = [
  '/background/patrick-langwallner-gbiGvTv9X_E-unsplash.jpg', // About hero (common landing page)
  '/background/cj-65kwzlfW6QI-unsplash.jpg', // Fund hero
  '/background/zhu-yunxiao-pGyL9zDMap4-unsplash.jpg', // Advisory hero
];

const NON_CRITICAL_IMAGES = [
  '/background/adam-borkowski-dH9Bk0I4EGM-unsplash.jpg',
  '/background/patrick-langwallner-LPv5CAFq0VI-unsplash.jpg',
  '/background/olivie-strauss-6zhPvQSVt3I-unsplash.jpg',
  '/background/laura-adai-DNZjccZ3Drg-unsplash.jpg',
  '/background/patrick-baum-oQTl_mHd2Ik-unsplash.jpg',
  '/background/polina-kuzovkova-sar-Y4BxZDA-unsplash.jpg',
  '/background/yuliya-matuzava-dGYW97m0voU-unsplash.jpg',
  '/background/giorgio-trovato-VavXiCDrOgA-unsplash.jpg',
];

// Page-specific image mapping for smart preloading
const PAGE_IMAGES: Record<string, string[]> = {
  '/about': ['/background/patrick-langwallner-gbiGvTv9X_E-unsplash.jpg'],
  '/fund': ['/background/cj-65kwzlfW6QI-unsplash.jpg'],
  '/advisory': ['/background/zhu-yunxiao-pGyL9zDMap4-unsplash.jpg', '/background/laura-adai-DNZjccZ3Drg-unsplash.jpg'],
  '/contact': ['/background/patrick-baum-oQTl_mHd2Ik-unsplash.jpg'],
  '/privacy-policy': ['/background/polina-kuzovkova-sar-Y4BxZDA-unsplash.jpg', '/background/yuliya-matuzava-dGYW97m0voU-unsplash.jpg', '/background/giorgio-trovato-VavXiCDrOgA-unsplash.jpg'],
};

export const ImagePreloader: React.FC = () => {
  const location = useLocation();
  const loadedImagesRef = useRef<Set<string>>(new Set());

  // Smart image loading function
  const loadImage = useCallback((src: string, priority: 'high' | 'low' = 'low'): Promise<void> => {
    return new Promise((resolve) => {
      if (loadedImagesRef.current.has(src)) {
        resolve();
        return;
      }

      const img = new Image();

      // Set loading priority for modern browsers
      if ('loading' in img) {
        (img as any).loading = priority === 'high' ? 'eager' : 'lazy';
      }

      img.onload = () => {
        loadedImagesRef.current.add(src);
        resolve();
      };

      img.onerror = () => {
        console.warn(`Failed to load image: ${src}`);
        resolve();
      };

      img.src = src;
    });
  }, []);

  // Load current page images immediately
  useEffect(() => {
    const currentPageImages = PAGE_IMAGES[location.pathname] || [];
    currentPageImages.forEach(src => loadImage(src, 'high'));
  }, [location.pathname, loadImage]);

  // Progressive loading strategy
  useEffect(() => {
    let mounted = true;

    const progressiveLoad = async () => {
      // Phase 1: Load critical images first (high priority)
      await Promise.all(
        CRITICAL_IMAGES.map(src => loadImage(src, 'high'))
      );

      if (!mounted) return;

      // Phase 2: Load non-critical images with delay (low priority)
      const loadNonCritical = async () => {
        for (const src of NON_CRITICAL_IMAGES) {
          if (!mounted) break;
          await loadImage(src, 'low');
          // Add small delay between loads to avoid blocking
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      };

      // Start non-critical loading after a delay
      setTimeout(loadNonCritical, 1000);
    };

    // Start progressive loading
    const timer = setTimeout(progressiveLoad, 200);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [loadImage]);

  // This component doesn't render anything
  return null;
};